/**
 * Shared bot-type metadata.
 *
 * Single source of truth for label/availability across Instances list,
 * MachineDetail picker, InstanceDetail header, etc.
 *
 * `available` reflects the rollout phase. Phase C unlocks claude_code;
 * Phase D will unlock codex; cursor/custom stay gated until designed.
 */

export type BotType = 'openclaw' | 'claude_code' | 'codex' | 'cursor' | 'custom';

export interface BotTypeSpec {
  value: BotType;
  label: string;
  available: boolean;
  /** Whether instances of this type need a workspace_dir / share URL. */
  requiresWorkspace: boolean;
  /** Whether instances of this type spawn a local subprocess vs. attach remote. */
  hasLocalProcess: boolean;
}

export const BOT_TYPES: BotTypeSpec[] = [
  { value: 'openclaw',    label: 'OpenClaw',    available: true,  requiresWorkspace: false, hasLocalProcess: true },
  { value: 'claude_code', label: 'Claude Code', available: true,  requiresWorkspace: true,  hasLocalProcess: true },
  { value: 'codex',       label: 'Codex',       available: true,  requiresWorkspace: true,  hasLocalProcess: true },
  { value: 'cursor',      label: 'Cursor',      available: false, requiresWorkspace: true,  hasLocalProcess: true },
  { value: 'custom',      label: 'Custom',      available: false, requiresWorkspace: false, hasLocalProcess: true },
];

const BY_VALUE: Record<string, BotTypeSpec> = Object.fromEntries(
  BOT_TYPES.map(b => [b.value, b]),
);

/** Display label for any bot_type string (including legacy 'claude-code'). */
export function botTypeLabel(type: string | undefined | null): string {
  if (!type) return 'Unknown';
  const norm = type === 'claude-code' ? 'claude_code' : type;
  return BY_VALUE[norm]?.label ?? (norm.charAt(0).toUpperCase() + norm.slice(1));
}

/** True if the bot type requires a workspace target (dir or share URL). */
export function botTypeRequiresWorkspace(type: string | undefined | null): boolean {
  if (!type) return false;
  const norm = type === 'claude-code' ? 'claude_code' : type;
  return BY_VALUE[norm]?.requiresWorkspace ?? false;
}
